import React from "react";
import { useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { useInstruction } from "@/hooks/useInstruction";
import { Field, TextareaField, SelectField, YesNo, CheckboxField, SectionTitle, SubHead } from "@/components/wizard/Fields";
import { ListEditor } from "@/components/wizard/Steps";
import { DisclaimerSection } from "@/components/DisclaimerSection";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronLeft } from "lucide-react";

const TITLES = ["Mr", "Mrs", "Miss", "Ms", "Dr", "Other"];
const grid = "grid gap-4 sm:grid-cols-2";

function Person({ v = {}, on, prefix, showDob = true }) {
  return (
    <div className={grid}>
      <SelectField label="Title" value={v.title} onChange={(x) => on("title", x)} options={TITLES} testId={`${prefix}-title`} />
      <Field label="Full name" value={v.fullName} onChange={(x) => on("fullName", x)} testId={`${prefix}-name`} />
      <Field label="Address" value={v.address} onChange={(x) => on("address", x)} testId={`${prefix}-address`} />
      <Field label="Postcode" value={v.postcode} onChange={(x) => on("postcode", x)} testId={`${prefix}-postcode`} />
      <Field label="Relationship to Donor" value={v.relationship} onChange={(x) => on("relationship", x)} testId={`${prefix}-rel`} />
      <Field label="Telephone" value={v.tel} onChange={(x) => on("tel", x)} testId={`${prefix}-tel`} />
      <Field label="Email" type="email" value={v.email} onChange={(x) => on("email", x)} testId={`${prefix}-email`} />
      {showDob && <Field label="Date of Birth" type="date" value={v.dob} onChange={(x) => on("dob", x)} testId={`${prefix}-dob`} />}
    </div>
  );
}

const HOW_ACT = [{ value: "jointly", label: "Jointly" }, { value: "jointly_severally", label: "Jointly and severally" }];

export default function LpaForm() {
  const navigate = useNavigate();
  const { data, setData, progress, saving, save, submit } = useInstruction("lpa");

  if (data === null)
    return <div className="min-h-screen flex items-center justify-center bg-brand-bg"><Loader2 className="w-8 h-8 animate-spin text-brand-green" /></div>;

  const patch = (section, obj) => setData((d) => ({ ...d, [section]: { ...(d[section] || {}), ...obj } }));
  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setList = (k, arr) => setData((d) => ({ ...d, [k]: arr }));
  const types = data.lpaTypes || [];
  const toggleType = (t) => setList("lpaTypes", types.includes(t) ? types.filter((x) => x !== t) : [...types, t]);
  const isHW = types.includes("health_welfare");
  const isPF = types.includes("property_finance");

  const complete = async () => {
    if (types.length === 0) { return; }
    if (await save(true)) navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-brand-bg">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <button onClick={() => navigate("/dashboard")} className="text-sm text-brand-muted hover:text-brand-green flex items-center gap-1 mb-4" data-testid="lpa-back">
          <ChevronLeft className="w-4 h-4" /> Back to dashboard
        </button>
        <div className="bg-white rounded-xl border border-brand-line p-6 md:p-10 shadow-sm space-y-2">
          <SectionTitle hint="Lasting Power of Attorney — £300 + VAT per type.">LPA Instruction</SectionTitle>

          <SubHead>Type(s) of LPA</SubHead>
          <CheckboxField label="LPA — Health & Welfare (£300 + VAT)" checked={isHW} onChange={() => toggleType("health_welfare")} testId="lpa-type-hw" />
          <CheckboxField label="LPA — Property & Financial Affairs (£300 + VAT)" checked={isPF} onChange={() => toggleType("property_finance")} testId="lpa-type-pf" />

          <SubHead>Donor 1</SubHead>
          <Person v={data.donor1 || {}} prefix="donor1" on={(k, val) => patch("donor1", { [k]: val })} />
          <div className="pt-3"><YesNo label="Is there a second donor (e.g. spouse)?" value={data.hasDonor2} onChange={(x) => setField("hasDonor2", x)} testId="lpa-has-donor2" /></div>
          {data.hasDonor2 && (<><SubHead>Donor 2</SubHead><Person v={data.donor2 || {}} prefix="donor2" on={(k, val) => patch("donor2", { [k]: val })} /></>)}

          <SubHead>Attorneys</SubHead>
          <ListEditor items={data.attorneys} setItems={(x) => setList("attorneys", x)} testId="attorney"
            blank={{ title: "", fullName: "", address: "", postcode: "", relationship: "", tel: "", email: "", dob: "" }}
            addLabel="Add an attorney"
            render={(it, i, update) => <Person v={it} prefix={`attorney-${i}`} on={(k, val) => update(i, k, val)} />} />
          <div className="pt-3"><SelectField label="How are attorneys to act?" value={data.howAttorneysAct} onChange={(x) => setField("howAttorneysAct", x)} options={HOW_ACT} testId="lpa-how-act" /></div>

          <SubHead>Replacement attorneys</SubHead>
          <ListEditor items={data.replacementAttorneys} setItems={(x) => setList("replacementAttorneys", x)} testId="replacement"
            blank={{ title: "", fullName: "", address: "", postcode: "", relationship: "", tel: "", email: "", dob: "" }}
            addLabel="Add a replacement attorney"
            render={(it, i, update) => <Person v={it} prefix={`replacement-${i}`} on={(k, val) => update(i, k, val)} />} />

          <SubHead>Options</SubHead>
          <div className="space-y-4">
            <SelectField label="When can the LPA be used?" value={data.whenUsed} onChange={(x) => setField("whenUsed", x)}
              options={[{ value: "when_registered", label: "As soon as it is registered" }, { value: "no_capacity", label: "Only if I lose mental capacity" }]} testId="lpa-when-used" />
            {isHW && (
              <SelectField label="Life-sustaining treatment (Health & Welfare)" value={data.lifeSustaining} onChange={(x) => setField("lifeSustaining", x)}
                options={[{ value: "option_a", label: "Option A — attorneys CAN give/refuse consent" }, { value: "option_b", label: "Option B — attorneys CANNOT" }]} testId="lpa-life-sustaining" />
            )}
            <SelectField label="Registration" value={data.registration} onChange={(x) => setField("registration", x)}
              options={[{ value: "immediately", label: "Register immediately" }, { value: "later", label: "Not to be registered now" }]} testId="lpa-registration" />
            {isPF && (
              <div className="grid sm:grid-cols-2 gap-4">
                <YesNo label="Cover digital assets?" value={data.digitalAssets} onChange={(x) => setField("digitalAssets", x)} testId="lpa-digital" />
                <YesNo label="Attorneys able to access Will?" value={data.accessWill} onChange={(x) => setField("accessWill", x)} testId="lpa-access-will" />
                <YesNo label="Continue any charitable donations?" value={data.charitable} onChange={(x) => setField("charitable", x)} testId="lpa-charitable" />
                <YesNo label="Power to place assets in discretionary managed accounts?" value={data.discretionaryManaged} onChange={(x) => setField("discretionaryManaged", x)} testId="lpa-discretionary" />
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <YesNo label="Previous LPA to be revoked?" value={data.revokePrevious} onChange={(x) => setField("revokePrevious", x)} testId="lpa-revoke" />
              <YesNo label="Fee remission to be applied for?" value={data.feeRemission} onChange={(x) => setField("feeRemission", x)} testId="lpa-fee-remission" />
            </div>
            <YesNo label="Should the consultant be the certificate provider?" value={data.consultantCertificate} onChange={(x) => setField("consultantCertificate", x)} testId="lpa-cert" />
            <TextareaField label="People to be notified (names & addresses)" value={data.peopleNotified} onChange={(x) => setField("peopleNotified", x)} testId="lpa-notified" />
          </div>

          <div className="pt-4"><SubHead>Declarations & Elected Services</SubHead>
            <DisclaimerSection variant="lpa" value={data.disclaimer || {}} onChange={(v) => setField("disclaimer", v)} />
          </div>

          {types.length === 0 && <p className="text-sm text-brand-error pt-2">Please select at least one LPA type.</p>}
        </div>

        <div className="flex items-center justify-between mt-6">
          <Button variant="ghost" onClick={() => save(false)} disabled={saving} className="text-brand-muted" data-testid="lpa-save-draft">Save draft</Button>
          <div className="flex gap-2">
            {progress?.submitted ? (
              <Button disabled className="bg-brand-green text-white">Submitted</Button>
            ) : (
              <>
                <Button variant="outline" onClick={complete} disabled={saving || types.length === 0} className="border-brand-line text-brand-green" data-testid="lpa-complete">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save & complete
                </Button>
                <Button onClick={async () => { if (types.length === 0) return; if (await save(true, true)) await submit(); }} disabled={saving || types.length === 0} className="bg-brand-green hover:bg-brand-greenHover text-white" data-testid="lpa-submit">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Submit instruction
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
